/**
 * Compute Utils
 * this file contains general computation for a specific value
 *
 * Naming Convention: get${Value}, is${Value}, has${Value}
 * i.e. getTouristName, isValid, hasBaggage
 */

import type { Env } from "@tajawal/web-flights-sdk/lib/types/common.types";
import type { ParsedUrlQuery } from "querystring";
import type { TravellerModel } from "@tajawal/web-flights-sdk/lib/types/traveller.types";
import {
  AIRLINE_IMAGE_URL,
  MIN_SEATS_REMAINING_THRESHOLD,
  SPECIAL_FORMAT_DIAL_CODES,
  AIRPORT_FILTER_TYPES,
} from "@/constants/common.constants";
import _get from "lodash/get";
import _some from "lodash/some";
import _includes from "lodash/includes";
import _values from "lodash/values";
import _replace from "lodash/replace";
import _isPlainObject from "lodash/isPlainObject";
import _isUndefined from "lodash/isUndefined";
import _filter from "lodash/filter";
import _toLower from "lodash/toLower";
import _toString from "lodash/toString";
import _toNumber from "lodash/toNumber";
import _split from "lodash/split";
import _join from "lodash/join";
import _pick from "lodash/pick";
import _reduce from "lodash/reduce";
import queryString from "query-string";
import _startsWith from "lodash/startsWith";
import _map from "lodash/map";
import _flatMap from "lodash/flatMap";
import _isNumber from "lodash/isNumber";
import _groupBy from "lodash/groupBy";
import { SearchRequestModel } from "@tajawal/web-flights-sdk/lib/types/search-request.types";
import { differenceInYears } from "date-fns";
import { getMonthNumber, toHijri } from "@/utils/date.utils";
import {
  FILTER_PARAMS_MAP,
  TRAVELLERS_TYPES_CODES_MAP,
  TRAVELLERS_TYPES_MAP,
} from "@tajawal/web-flights-sdk/lib/constants";
import type FlightInfoStore from "@tajawal/web-flights-sdk/lib/stores/flight-info.store";
import { Icons } from "@tajawal/ct-web-design-system";
import { IconProps } from "@tajawal/ct-web-design-system/dist/images/types";
import { TFunction } from "i18next";
import { CAMPAIGN_PARAMS } from "@/constants/analytics.constants";
import { getAirportDisplayContent } from "./content.utils";
import { LegModel } from "@tajawal/web-flights-sdk/lib/types/leg.types";
import {
  DATE_INPUT_FIELDS,
  DATE_WITH_TIME_FORMAT,
} from "@/constants/date.constants";
import format from "date-fns/format";
import { asUTC } from "@tajawal/web-core-sdk-v2";
import _concat from "lodash/concat";
import { APP_PAGES } from "@tajawal/web-core-sdk-v2/lib/constants";
import {
  BaggagePerTraveller,
  FareCalendarCellProps,
} from "@/types/common.types";
import { getFareCalendarCellTestID } from "@/app-desktop/containers/FareCalendar/utils/test-ids.utils";
import { DateInputField, DateInputOption } from "@/types/date.types";
import { getTravellerHashKey } from "./form.utils";
import {
  FrequentFlyerLegOptions,
  SpecialServiceLegOption,
} from "@/types/content.types";
import { LegBaggageInfo } from "@tajawal/web-flights-sdk/lib/types/baggage.types";
import { Dictionary } from "lodash";
import { SelectedLuggageModel } from "@tajawal/web-flights-sdk/lib/types/luggage.types";
import { SelectFilter } from "@tajawal/web-flights-sdk/lib/types/filters/select-filter.types";
import {
  DomesticItinerary,
  ItineraryModel,
  ItineraryPrice,
} from "@tajawal/web-flights-sdk/lib/types/itinerary.types";
import {
  checkDomestic,
  checkMultiCity,
  checkEconomyCabin,
  checkRoundTrip,
  checkOneWay,
  getParsedSearchQuery,
  getRouteFromQuery,
  getCabinTypeFromQuery,
  getSearchTypeFromQuery,
  getReturnDateFromQuery,
  getDepartureDateFromQuery,
} from "@tajawal/web-flights-sdk/lib/utils/search.utils";
import { checkDomesticKSA } from "@tajawal/web-flights-sdk/lib/utils/search.utils";

export function isProduction(): boolean {
  return process.env.NODE_ENV === "production";
}

export function isSSR(): boolean {
  return typeof window === "undefined";
}

export function isValidJwt(token: string): boolean {
  const JWT_REGEX = /^[A-Za-z0-9-_]*\.[A-Za-z0-9-_]*\.[A-Za-z0-9-_]*$/;
  return JWT_REGEX.test(token);
}

export function getSaleId(query: ParsedUrlQuery) {
  const { saleId } = query;
  if (typeof saleId === "string") return saleId;
  if (Array.isArray(saleId) && saleId.length > 0) return saleId[0];
  return "";
}

export function generateAirlineImageUrl(code: string) {
  return `${AIRLINE_IMAGE_URL}/${code}.png`;
}

export function generateTealiumEventConfigPayload($env: Env) {
  return {
    platform: "web",
    pos: _get($env, "config.brand"),
    site_language: _get($env, "config.language"),
    site_currency: _get($env, "currency.currentCurrency"),
  };
}

export function generateTealiumEventSearchPayload({
  searchRequest,
  $resources,
}: {
  searchRequest: SearchRequestModel;
  $resources: FlightInfoStore["$resources"];
}) {
  if (!_isPlainObject(searchRequest)) return {};

  const { originId, destinationId, pax } = searchRequest;

  return {
    origin_iata: originId,
    destination_iata: destinationId,
    origin_cityName: $resources.getAirportCity(originId as string),
    destination_cityName: $resources.getAirportCity(destinationId as string),
    origin_countryName: $resources.getAirportCountry(originId as string),
    destination_countryName: $resources.getAirportCountry(
      destinationId as string,
    ),
    cabin_class: searchRequest.preferredCabin,
    flight_type: searchRequest.isDomestic ? "dom" : "int",
    trip_type: searchRequest.searchType,
    departure_date: searchRequest.departureDate,
    return_date: searchRequest.returnDate,
    total_pax: searchRequest.totalPax,
    adult_pax: _get(pax, "adult", 0),
    child_pax: _get(pax, "child", 0),
    infant_pax: _get(pax, "infant", 0),
  };
}

export function generateEventCampaignPayload() {
  const capaignParams = queryString.parse(window.location.search);
  return _pick(capaignParams, CAMPAIGN_PARAMS);
}

export function getTrimmedPhoneNumber(dialCode: string, value: string) {
  if (_includes(SPECIAL_FORMAT_DIAL_CODES, dialCode) && value) {
    return _replace(value, /^0+|-|\s|_/g, "");
  }
  return _replace(value, /-|\s|_/g, "");
}
export function getUpdatedContactState(state) {
  if (!_isPlainObject(state)) return state;
  const dialCode = _get(state, "dialCode");
  const phone = _get(state, "phone");
  const trimmedPhone = getTrimmedPhoneNumber(dialCode, phone);
  return { ...state, phone: trimmedPhone };
}

export function getFieldIdFromName(fieldName: string) {
  return _replace(fieldName, /\./g, "_");
}

// TODO update types
export function filterSavedTravellersByFullName({
  traveller,
  savedTravellers,
  parentForm,
}) {
  const { firstName, middleName, lastName, title, type } = traveller;
  const travellersFormHash = _get(
    parentForm.getState(),
    "values.travellersFormHash",
    {},
  );
  const travellerHash = travellersFormHash[traveller.id];

  const savedTravellersHash = _get(
    parentForm.getState(),
    "values.savedTravellersHash",
    {},
  );

  if (
    travellerHash ===
    savedTravellersHash[type]?.[getTravellerHashKey(traveller)]
  ) {
    return savedTravellers;
  }

  const travellersHashList = _values(travellersFormHash);

  return _filter(
    savedTravellers,
    (traveller) =>
      !_includes(
        travellersHashList,
        savedTravellersHash[type]?.[getTravellerHashKey(traveller)],
      ) &&
      _toLower(traveller.firstName).includes(_toLower(firstName)) &&
      _toLower(traveller.title).includes(_toLower(title)) &&
      _toLower(traveller.middleName).includes(_toLower(middleName)) &&
      _toLower(traveller.lastName).includes(_toLower(lastName)),
  );
}

export function isAdultTraveller(
  startDate: Date | string,
  birthDate: Date,
  isHijri: boolean,
) {
  const _startDate =
    typeof startDate === "string" ? new Date(startDate) : startDate;
  const start = isHijri ? toHijri(_startDate) : _startDate;
  return differenceInYears(start, birthDate) >= 16;
}

export function isMinorTraveller(
  startDate: Date | string,
  birthDate: Date,
  isHijri: boolean,
) {
  const _startDate =
    typeof startDate === "string" ? new Date(startDate) : startDate;
  const start = isHijri ? toHijri(_startDate) : _startDate;
  const diff = differenceInYears(start, birthDate);
  return diff < 16 && diff >= 12;
}

export function getContactFromUser(user) {
  if (!_isPlainObject(user)) return {};
  return {
    ...user,
    phone: _get(user, "phone.number"),
    dialCode: _get(user, "phone.code"),
  };
}

// TODO double check travellers' type
export function filterTravellersByFullNameForContact({
  travellers,
  contact,
}: {
  travellers: TravellerModel[];
  contact: Record<string, any>;
}) {
  const { firstName, lastName } = contact;

  const nameMatchedTravellers = _filter(
    travellers,
    (traveller) =>
      traveller.type === TRAVELLERS_TYPES_MAP.ADULT.code &&
      !_isUndefined(traveller.firstName) &&
      !_isUndefined(traveller.lastName) &&
      (_toLower(traveller.firstName) !== _toLower(firstName) ||
        _toLower(traveller.lastName) !== _toLower(lastName)),
  );

  const nameIncludedTravellers =
    (firstName && firstName.length < 3) || (lastName && lastName.length < 3)
      ? _filter(
          travellers,
          (traveller) =>
            traveller.type === TRAVELLERS_TYPES_MAP.ADULT.code &&
            !_isUndefined(traveller.firstName) &&
            !_isUndefined(traveller.lastName) &&
            (_toLower(traveller.firstName).includes(_toLower(firstName)) ||
              _toLower(traveller.lastName).includes(_toLower(lastName))),
        )
      : [];

  return nameIncludedTravellers.length > 0
    ? nameIncludedTravellers
    : nameMatchedTravellers;
}

export function getNameSegments(value: string): string[] {
  const values = _split(value, " ");
  const firstName = values[0];
  const lastName = _join(values.slice(1), " ");
  return [firstName, lastName];
}
export function getContactPhoneNumberInputFormat(
  dialCode: string,
  value: string,
  direction = "ltr",
): string {
  if (!value) return;

  const separator = direction === "ltr" ? " - " : "";
  const specialCountries = {
    "+971": {
      country: "AE",
      formatFunction: specialFormatPhoneNumber,
    },
    "+966": {
      formatFunction: specialFormatPhoneNumber,
    },
    "+968": {
      country: "OM",
      formatFunction: standardFormatPhoneNumber,
    },
    "+974": {
      country: "QA",
      formatFunction: standardFormatPhoneNumber,
    },
    "+973": {
      country: "BH",
      formatFunction: standardFormatPhoneNumber,
    },
    "+962": {
      country: "JO",
      formatFunction: standardFormatPhoneNumber,
    },
    "+965": {
      country: "KW",
      formatFunction: standardFormatPhoneNumber,
    },
    "+20": {
      country: "EG",
      formatFunction: standardFormatPhoneNumber,
    },
  };

  if (specialCountries[dialCode]) {
    return specialCountries[dialCode].formatFunction(
      dialCode,
      value,
      separator,
    );
  }
}

export function specialFormatPhoneNumber(
  dialCode: string,
  value: string,
  separator: string,
) {
  return _startsWith(value, "0")
    ? `#${separator}##${separator}###${separator}####`
    : `##${separator}###${separator}####`;
}

export function standardFormatPhoneNumber(
  dialCode: string,
  value: string,
  separator: string,
) {
  const len = value.length;
  if (len <= 2) return `###`;
  if (len <= 5) return `###${separator}###`;
  return `###${separator}###${separator}####`;
}

export function getFullName({
  title,
  firstName,
  lastName,
}: Record<"title" | "firstName" | "lastName", string>): string {
  return `${title}. ${firstName} ${lastName}`;
}

export const getFieldNameFactory =
  (index: number) =>
  (key?: string): string =>
    key ? `travellers.${index}.${key}` : `travellers.${index}`;

export function getStopsText(stopsLength: number, t: TFunction): string {
  if (stopsLength === 0) {
    return t("Common.Direct");
  }

  return t("Common.Stop", { count: stopsLength || 1, stops: stopsLength });
}

export function getAppFeatureList(
  t: TFunction,
): { icon: React.FC<IconProps>; label: string }[] {
  return [
    { icon: Icons.UsersTwo, label: t("DownloadAppDrawerV2.Speed_Up_Bookings") },
    { icon: Icons.Discount, label: t("DownloadAppDrawerV2.Get_Offers") },
    { icon: Icons.WalletIcon, label: t("DownloadAppDrawerV2.Earn_Points") },
    { icon: Icons.Calendar, label: t("DownloadAppDrawerV2.Get_Inspiration") },
  ];
}

export function isServerError(code: string): boolean {
  return String(code)[0] === "5";
}

export function getSearchBoxProps(searchRequest, $resources, url) {
  const urlSearchParams = queryString.parse(url);
  const legs = _reduce(
    _get(searchRequest, "leg"),
    (legs, leg, index) => {
      const nextLeg = _get(searchRequest, `leg.${index + 1}`);
      const originIata = _get(leg, "originId", "");
      const destinationIata = _get(leg, "destinationId", "");
      return [
        ...legs,
        {
          origin: originIata
            ? getAirportDisplayContent($resources?.getAirport(originIata))
            : "",
          originIata,
          destination: destinationIata
            ? getAirportDisplayContent($resources?.getAirport(destinationIata))
            : "",
          destinationIata,
          nearbyOrigin: !!_get(leg, "nearByOriginId", false),
          nearbyDestination: !!_get(leg, "nearByDestinationId", false),
          departureDate: _get(leg, "departure", new Date()),
          returnDate: _get(nextLeg, "departure", new Date()),
        },
      ];
    },
    [],
  );

  return {
    legs,
    searchType: _get(searchRequest, "searchType", "").toLowerCase(),
    cabin: _get(searchRequest, "preferredCabin", ""),
    infants: _get(searchRequest, "pax.infant", 0),
    children: _get(searchRequest, "pax.child", 0),
    adults: _get(searchRequest, "pax.adult", 1),
    nonstop: _get(searchRequest, "leg.0.nonstop", false),
    isModifySearch: true,
    directFlightsOnly: urlSearchParams[FILTER_PARAMS_MAP.stop] === "0",
    hasBaggageOnly: !!urlSearchParams[FILTER_PARAMS_MAP.hasBaggageOnly],
    flexibleDate: _get(searchRequest, "flexibleDate", false),
  };
}

export function constructSearchUrl({
  query,
  searchString = null,
  isMobile = true,
  lang = "en",
}) {
  const urlBase = "flights";
  const url = [urlBase, query].join("/");
  if (typeof searchString === "string" && searchString[0] === "?") {
    return `/${isMobile ? "mweb" : lang}/${url}${searchString}`;
  }
  return `/${isMobile ? "mweb" : lang}/${url}`;
}

export function getAbTestingHeaderKey(keyName: string) {
  if (!keyName) return "";
  return `x-ab-${keyName}`;
}

export function getAbTestingHeader(key: string, treatment: string) {
  if (key && treatment !== "off") {
    return { [getAbTestingHeaderKey(key)]: treatment };
  }

  return {};
}

export function getFareCalendarItemProps({
  cellProps,
  selectedCellProps,
  hoveredKey,
}): FareCalendarCellProps {
  const { departureDate, returnDate, pricingKey } = cellProps;
  const selected = selectedCellProps?.pricingKey === pricingKey;
  const [hoveredDeparture, hoveredReturn] = _split(hoveredKey, "_");
  const itemProps = {
    key: pricingKey,
    ...cellProps,
    selected,
    hovered: hoveredKey && hoveredKey === pricingKey,
  };

  // round trip related props
  if (returnDate) {
    itemProps.inSelectedRange =
      (departureDate === selectedCellProps?.departureDate &&
        returnDate <= selectedCellProps?.returnDate) ||
      (returnDate === selectedCellProps?.returnDate &&
        departureDate >= selectedCellProps?.departureDate);
    itemProps.inHoveredRange =
      (departureDate === hoveredDeparture && returnDate <= hoveredReturn) ||
      (returnDate === hoveredReturn && departureDate >= hoveredDeparture);
  }

  itemProps.testId = getFareCalendarCellTestID(itemProps);

  return itemProps;
}

export function getFormatedLegsForEvent(
  legs: LegModel[] = [],
  $resources: FlightInfoStore["$resources"],
) {
  return _map(
    legs,
    (
      {
        originId,
        destinationId,
        duration,
        departureTime,
        arrivalTime,
      }: LegModel,
      index: number,
    ) => {
      return JSON.stringify({
        index,
        origin: originId,
        origin_airport: $resources.getAirportCity(originId),
        destination: destinationId,
        destination_airport: $resources.getAirportCity(originId),
        departure_datetime: format(
          asUTC(new Date(departureTime)),
          DATE_WITH_TIME_FORMAT,
        ),
        arrival_datetime: format(
          asUTC(new Date(arrivalTime)),
          DATE_WITH_TIME_FORMAT,
        ),
        total_duration: duration?.value,
      });
    },
  );
}

export function getCurrentSelectedFilterValues(state, changedCode, isSelected) {
  return _concat(
    _filter(state, (c) => c !== changedCode),
    isSelected ? [] : [changedCode],
  );
}

export function getServiceFeeABConfig(router, isRoundTrip) {
  return {
    route: router?.query?.query?.[0],
    cabin_class: isRoundTrip
      ? router?.query?.query?.[3]
      : router?.query?.query?.[2],
  };
}

export function isNotHomePage(page: string): boolean {
  return [
    APP_PAGES.FLIGHTS_RESULTS,
    APP_PAGES.FLIGHTS_PAX,
    APP_PAGES.FLIGHTS_EXTRAS,
  ].includes(page);
}

export function isPreBookingPage(page: string): boolean {
  return [APP_PAGES.FLIGHTS_PAX, APP_PAGES.FLIGHTS_EXTRAS].includes(page);
}

export function getSelectFlightEventDetail(selectedItinerary) {
  const legs = _get(selectedItinerary, "itinerary.legs");
  const aircraftTypes = _flatMap(legs, (leg) =>
    _map(_get(leg, "segments"), "aircraft"),
  );

  if (_includes(aircraftTypes, "BUS")) {
    return "bus-selected";
  }
  if (_includes(aircraftTypes, "TRN")) {
    return "Train-selected";
  }
  return "";
}

export function dateOptionFormatter(
  option: string | number,
  type: DateInputField,
  isHijri: boolean,
  t: TFunction,
): DateInputOption {
  if (type === DATE_INPUT_FIELDS.MONTH) {
    const dateNumber = getMonthNumber(option as string, isHijri, t);
    return {
      label: `${dateNumber + 1} - ${_toString(option)}`,
      value: getMonthNumber(option as string, isHijri, t),
    };
  }
  return {
    label: _toString(option),
    value: _toNumber(option),
  };
}

export function getAddedBaggagePerTravellerAndLeg(
  travellers: TravellerModel[],
  itineraryId: string,
  baggageInfo: LegBaggageInfo,
  isMultiPNR: boolean,
): BaggagePerTraveller[] {
  const mappedBaggage = _map(travellers, (traveller) => {
    if (traveller.type !== TRAVELLERS_TYPES_CODES_MAP.INF) {
      const getterKeyPrefix = isMultiPNR
        ? `products.${baggageInfo.productId}.ssr.luggage`
        : "ssr.luggage";
      const defaultItinerary = _get(
        traveller,
        `${getterKeyPrefix}[${itineraryId}]`,
        false,
      );
      const luggage = _get(
        traveller,
        `${getterKeyPrefix}[${baggageInfo?.legId}]`,
        defaultItinerary,
      );

      if (luggage) {
        return {
          type: traveller.type,
          indexByType: traveller.indexByType,
          luggage,
        } as BaggagePerTraveller;
      }
    }
    return null;
  });

  return _filter(mappedBaggage, Boolean) as BaggagePerTraveller[];
}

export function getMutiPNRSsrField(productId = "") {
  return `products.${productId}.ssr`;
}
export function getOperatorCodeField(productId = "") {
  return productId
    ? `products.${productId}.ssr.frequentFlyer.operatorCode`
    : "ssr.frequentFlyer.operatorCode";
}
export function getOperatorNumberField(productId = "") {
  return productId
    ? `products.${productId}.ssr.frequentFlyer.number`
    : "ssr.frequentFlyer.number";
}

export function getFrequentFlyerOptions(
  isMultiPNR: boolean,
  multiFrequentFlyer: Dictionary<any>,
  $resources: FlightInfoStore["$resources"],
  t: TFunction,
): FrequentFlyerLegOptions {
  if (!isMultiPNR) {
    return [
      {
        isValid: true,
        operatorCodeField: getOperatorCodeField(),
        operatorNumberField: getOperatorNumberField(),
      },
    ];
  }

  const mappedMultiFrequentFlyer = _map(multiFrequentFlyer, (product) => {
    const {
      originId,
      destinationId,
      validatingCarrier,
      productId,
      isFrequentFlyerValid: isValid,
    } = product;
    return {
      isValid,
      productId,
      title: `${$resources.getAirportCity(originId)} ${t(
        "Common.To",
      )} ${$resources.getAirportCity(
        destinationId,
      )} - ${$resources.getAirlineLabel(validatingCarrier)}`,
      operatorCodeField: getOperatorCodeField(productId),
      operatorNumberField: getOperatorNumberField(productId),
      origin: originId,
      destination: destinationId,
      validatingCarrier,
    };
  });

  // special case: exclude the second frequent flyer if we have the same validatingCarrier and return the first one
  const validatingCarrierFields = _map(
    mappedMultiFrequentFlyer,
    "validatingCarrier",
  );
  const hasDuplicateValidatingCarrier =
    validatingCarrierFields.length !== new Set(validatingCarrierFields).size;
  if (hasDuplicateValidatingCarrier) {
    return [mappedMultiFrequentFlyer?.[0]];
  }

  return mappedMultiFrequentFlyer;
}

export function getSpecialServiceLegs(
  isMultiPNR: boolean,
  traveller: TravellerModel,
  $resources: FlightInfoStore["$resources"],
  t: TFunction,
): SpecialServiceLegOption[] {
  if (!isMultiPNR) {
    return [
      {
        isValid: true,
        mealFieldName: "ssr.meal",
        specialAssistanceFieldName: "ssr.specialRequest",
      },
    ];
  }

  return _map(traveller?.products, (product) => {
    const {
      originId,
      destinationId,
      validatingCarrier,
      productId,
      isMealValid: isValid,
    } = product;
    return {
      isValid,
      productId,
      title: `${$resources.getAirportCity(originId)} ${t(
        "Common.To",
      )} ${$resources.getAirportCity(
        destinationId,
      )} - ${$resources.getAirlineLabel(validatingCarrier)}`,
      mealFieldName: `products.${productId}.ssr.meal`,
      specialAssistanceFieldName: `products.${productId}.ssr.specialRequest`,
    };
  });
}

export function getFFInitialValue(
  traveller: TravellerModel,
  isMultiPNR: boolean,
) {
  if (!isMultiPNR) {
    const frequentFlyer = _get(traveller, "ssr.frequentFlyer");
    return frequentFlyer ? [frequentFlyer] : [];
  }

  return _map(traveller?.products, "ssr.frequentFlyer");
}

export function getSecialAssistanceInitialValue(
  traveller: TravellerModel,
  isMultiPNR: boolean,
) {
  if (isMultiPNR) {
    return _some(
      traveller?.products,
      (payload) =>
        _isPlainObject(payload) &&
        (_get(payload, "ssr.meal.value") ||
          _get(payload, "ssr.specialRequest.value")),
    );
  }

  return (
    _get(traveller, "ssr.meal.value") ||
    _get(traveller, "ssr.specialRequest.value")
  );
}

export function getBaggageFieldName(productId: string, isMultiPNR: boolean) {
  return productId && isMultiPNR
    ? `products.${productId}.ssr.luggage`
    : "ssr.luggage";
}

export function getExtraBaggage(
  traveller: TravellerModel,
  isMultiPNR?: boolean,
  productId?: string,
): Record<string, SelectedLuggageModel> {
  return _get(traveller, getBaggageFieldName(productId, isMultiPNR));
}

export function getBaggageFieldNames(
  traveller: TravellerModel,
  isMultiPNR: boolean,
) {
  return isMultiPNR
    ? _map(
        traveller?.products,
        ({ productId }) => `products.${productId}.ssr.luggage`,
      )
    : ["ssr.luggage"];
}

export function getExtraBaggageList(ssrPayload: any, isMultiPNR: boolean) {
  const extraBaggage = isMultiPNR
    ? _flatMap(ssrPayload, (payload) => _get(payload, "items.0.luggage"))
    : _flatMap(ssrPayload, (payload) => _get(payload, "luggage"));
  return _filter(extraBaggage);
}

export function isSeatsRemainingAvailable(treatment: string, isReady = true) {
  return isReady && treatment !== "off";
}

export function isSeatsRemainingThreshold(
  seatsAvailable: number,
  isPaxPage: boolean,
) {
  const isSeatDefined = _isNumber(seatsAvailable);
  return (
    isSeatDefined &&
    seatsAvailable <= MIN_SEATS_REMAINING_THRESHOLD &&
    !isPaxPage
  );
}

export function getAirportFilterSections({
  originId,
  state,
  resources,
}: {
  originId: string;
  state: SelectFilter[];
  resources: FlightInfoStore["$resources"];
}) {
  return _groupBy(state, (airport) => {
    const { code } = airport;
    const isAirport = resources.isAirport(String(code));
    if (isAirport && originId === code) {
      return AIRPORT_FILTER_TYPES.AIRPORT;
    }
    if (isAirport && originId !== code) {
      return AIRPORT_FILTER_TYPES.NEAR_BY;
    }
    if (!isAirport) {
      return AIRPORT_FILTER_TYPES.OTHERS;
    }
    return AIRPORT_FILTER_TYPES.AIRPORT;
  });
}

export function getTotalEventPrice(
  itinerary: ItineraryModel | DomesticItinerary,
  isRoundTrip: boolean,
) {
  const { displayCombinedPrice } = itinerary as DomesticItinerary;
  const { price: itineraryPrice } = itinerary as ItineraryModel;

  const price = isRoundTrip ? displayCombinedPrice : itineraryPrice;
  const { total: totalPrice = 0, roundtripTotal } = (price ||
    {}) as ItineraryPrice<string>;
  const priceToSend = String(isRoundTrip ? roundtripTotal : totalPrice).replace(
    /[,\s]/,
    "",
  );

  return parseFloat(priceToSend);
}

export function getCommonAVOParams(query: string) {
  const parsedSearchQuery = getParsedSearchQuery(query);
  const route = getRouteFromQuery(query);
  const [originId, destinationId] = _split(route, "-");
  const { adult, child, infant } = parsedSearchQuery;
  return {
    isDomesticKSA: checkDomesticKSA(query),
    isDomestic: checkDomestic(query),
    isEconomy: checkEconomyCabin(query),
    isMultiCity: checkMultiCity(query),
    query: query,
    isRoundTrip: checkRoundTrip(query),
    isOneWay: checkOneWay(query),
    adult,
    child,
    infant,
    route: getRouteFromQuery(query),
    cabinType: getCabinTypeFromQuery(query),
    tripType: getSearchTypeFromQuery(query),
    totalPax: adult + child + infant,
    originId,
    destinationId,
  };
}

export function isDateChanged(currentQuery: string, newQuery: string) {
  const departureDate = getDepartureDateFromQuery(currentQuery);
  const returnDate = getReturnDateFromQuery(currentQuery);
  const newDepartureDate = getDepartureDateFromQuery(newQuery);
  const newReturnDate = getReturnDateFromQuery(newQuery);
  return departureDate !== newDepartureDate || returnDate !== newReturnDate;
}
